import { ReactNode, useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FocusScope } from "react-aria";
import tw from "tailwind-styled-components";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";

import { PageScrollProvider } from "context/PageScrollContext";
import Sidebar from "./Sidebar";

// @ts-ignore
import LogoWithType from "svg/logo-with-type.svg";

/** The centered content column inside the portal panel. One 820px measure for
 * every portal page, so headers and content line up from page to page. */
export const PortalContent = tw.div`
  mx-auto
  w-full
  max-w-[820px]
  px-5
  pt-8
  pb-16
  sm:px-9
  sm:pt-10
`;

/** The signed-in portal shell. From lg up: a fixed oat sidebar on the left and
 * the page content in a rounded ivory panel inset from the viewport edges; the
 * panel scrolls internally and the shell itself never scrolls. Below lg: a top
 * bar and slide-in drawer replace the sidebar, the content spans the full
 * width, and the page scrolls normally. */
export default function PortalLayout({ children }: { children: ReactNode }) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const location = useLocation();
  const openButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isDrawerOpen) return;

    // Deferred a frame: the drawer is visibility-hidden until its open
    // transition starts, and a hidden element silently refuses focus.
    const focusFrame = requestAnimationFrame(() => {
      closeButtonRef.current?.focus({ preventScroll: true });
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsDrawerOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    const openButton = openButtonRef.current;

    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
      // The menu button scrolls away with the page; restoring focus to it
      // must not yank the reader back to the top.
      openButton?.focus({ preventScroll: true });
    };
  }, [isDrawerOpen]);

  return (
    <>
      <Helmet>
        <meta name="theme-color" content="#f8f6f4" />
      </Helmet>

      <div className="flex min-h-screen flex-col bg-oat-50 lg:h-screen lg:flex-row lg:supports-[height:100dvh]:h-dvh">
        <aside className="hidden w-[248px] flex-none overflow-y-auto lg:block">
          <Sidebar />
        </aside>

        <div className="flex h-14 flex-none items-center justify-between border-b border-oat-200 px-4 lg:hidden">
          <Link to="/" aria-label="After home">
            <LogoWithType height="20px" className="text-soil" />
          </Link>
          <button
            type="button"
            onClick={() => setIsDrawerOpen(true)}
            aria-label="Open menu"
            ref={openButtonRef}
            className="-mr-2 rounded-md p-2 text-soil transition-colors hover:bg-oat"
          >
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* The drawer stays mounted and slides off-canvas so the open/close
            transition can run both ways; the visibility transition takes it
            out of the tab order and accessibility tree once the slide-out
            finishes. */}
        <div
          className={`fixed inset-0 z-[60] bg-evergreen/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
            isDrawerOpen ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
          aria-hidden="true"
          onClick={() => setIsDrawerOpen(false)}
        />
        <div
          className={`fixed left-0 top-0 z-[70] h-full w-72 max-w-[85vw] bg-oat-50 lg:hidden ${
            isDrawerOpen
              ? "visible translate-x-0 shadow-2xl [transition:transform_300ms]"
              : "invisible -translate-x-full [transition:transform_300ms,visibility_0s_300ms]"
          }`}
          role="dialog"
          aria-modal={isDrawerOpen || undefined}
          aria-label="Menu"
        >
          <FocusScope contain={isDrawerOpen}>
            <button
              type="button"
              onClick={() => setIsDrawerOpen(false)}
              aria-label="Close menu"
              ref={closeButtonRef}
              className="absolute right-2 top-4 rounded-md p-2 text-soil transition-colors hover:bg-oat"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <div className="h-full overflow-y-auto">
              <Sidebar onNavigate={() => setIsDrawerOpen(false)} />
            </div>
          </FocusScope>
        </div>

        <main className="flex flex-1 lg:min-h-0 lg:p-3.5 lg:pl-0">
          <div
            ref={panelRef}
            className="flex min-w-0 flex-1 flex-col overflow-x-clip bg-ivory-light lg:overflow-y-auto lg:overflow-x-hidden lg:rounded-[18px] lg:border lg:border-oat-200"
          >
            <PageScrollProvider containerRef={panelRef}>
              {children}
            </PageScrollProvider>
          </div>
        </main>
      </div>
    </>
  );
}

/** The portal shell as a layout route: it stays mounted while the pages under
 * it change, so only the content area loads on navigation. */
export function PortalShell() {
  return (
    <PortalLayout>
      <Outlet />
    </PortalLayout>
  );
}
